(function () {
  'use strict';

  const getMainImage = (gallery) =>
    gallery.querySelector('[data-gallery-main]') || gallery.querySelector('.product-gallery__main-image');

  const getLightbox = () => document.querySelector('[data-gallery-lightbox]');

  const closeLightbox = (lightbox) => {
    if (!lightbox) return;
    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('gallery-lightbox-open');
    document.body.style.overflow = '';
  };

  const bindLightbox = () => {
    const lightbox = getLightbox();
    if (!lightbox || lightbox.dataset.galleryLightboxInit === 'true') return lightbox;

    lightbox.dataset.galleryLightboxInit = 'true';

    lightbox.querySelectorAll('[data-gallery-lightbox-close]').forEach((el) => {
      el.addEventListener('click', () => closeLightbox(lightbox));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && lightbox.classList.contains('is-open')) {
        closeLightbox(lightbox);
      }
    });

    return lightbox;
  };

  const openLightbox = (src, alt) => {
    if (!src) return;

    const lightbox = bindLightbox();
    if (!lightbox) return;

    const image = lightbox.querySelector('[data-gallery-lightbox-image]');
    const closeButton = lightbox.querySelector('[data-gallery-lightbox-close]');

    if (image) {
      image.classList.remove('is-loaded');
      image.src = src;
      image.alt = alt || '';

      if (image.complete) {
        image.classList.add('is-loaded');
      } else {
        image.onload = () => image.classList.add('is-loaded');
        image.onerror = () => image.classList.add('is-loaded');
      }
    }

    lightbox.classList.add('is-open');
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.classList.add('gallery-lightbox-open');
    document.body.style.overflow = 'hidden';
    closeButton?.focus();
  };

  const getZoomSource = (image, fallbackThumb) => {
    if (!image) return fallbackThumb?.dataset.zoomSrc || fallbackThumb?.dataset.src || '';
    return (
      image.dataset.galleryZoomSrc ||
      image.dataset.zoomSrc ||
      fallbackThumb?.dataset.zoomSrc ||
      image.currentSrc ||
      image.src ||
      fallbackThumb?.dataset.src ||
      ''
    );
  };

  const swapMainImage = (mainImage, src, alt, srcset, zoomSrc) => {
    if (!mainImage || !src) return;
    mainImage.classList.add('is-changing');
    window.setTimeout(() => {
      mainImage.src = src;
      mainImage.alt = alt || '';
      if (srcset) {
        mainImage.srcset = srcset;
      } else {
        mainImage.removeAttribute('srcset');
        mainImage.removeAttribute('sizes');
      }
      if (mainImage.dataset.galleryZoomSrc !== undefined) {
        mainImage.dataset.galleryZoomSrc = zoomSrc || src;
      }
      mainImage.classList.remove('is-changing');
    }, 120);
  };

  const initProductGallery = (root = document) => {
    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.galleryInit === 'true') return;
      gallery.dataset.galleryInit = 'true';

      const mainImage = getMainImage(gallery);
      const stage = gallery.querySelector('.product-gallery__stage');
      const thumbsWrap = gallery.querySelector('.product-gallery__thumbs-wrap');
      const thumbsCol = gallery.querySelector('[data-gallery-thumbs-col]');
      const thumbsPosition = gallery.dataset.thumbsPosition || 'left';
      const isBottomThumbs = thumbsPosition === 'bottom';
      const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
      const mobileTrack = gallery.querySelector('[data-gallery-mobile-track]');
      const mobileSlides = mobileTrack ? [...mobileTrack.querySelectorAll('[data-gallery-mobile-slide]')] : [];
      const zoomImages = [...gallery.querySelectorAll('[data-gallery-zoom-image]')];
      const dots = [...gallery.querySelectorAll('[data-gallery-dot]')];
      const counter = gallery.querySelector('[data-gallery-counter]');

      let activeIndex = thumbs.findIndex((thumb) => thumb.classList.contains('is-active'));
      if (activeIndex < 0) activeIndex = 0;

      const syncThumbsHeight = () => {
        if (!stage || !thumbsWrap || isBottomThumbs) return;
        thumbsWrap.style.setProperty('--gallery-thumbs-max-height', `${stage.offsetHeight}px`);
      };

      const scrollActiveThumbIntoView = () => {
        if (!thumbsCol) return;
        const activeThumb = thumbs[activeIndex];
        if (!activeThumb) return;

        if (isBottomThumbs) {
          const left = activeThumb.offsetLeft;
          const right = left + activeThumb.offsetWidth;
          const viewLeft = thumbsCol.scrollLeft;
          const viewRight = viewLeft + thumbsCol.clientWidth;
          if (left < viewLeft) {
            thumbsCol.scrollTo({ left, behavior: 'smooth' });
          } else if (right > viewRight) {
            thumbsCol.scrollTo({ left: right - thumbsCol.clientWidth, behavior: 'smooth' });
          }
          return;
        }

        const top = activeThumb.offsetTop;
        const bottom = top + activeThumb.offsetHeight;
        const viewTop = thumbsCol.scrollTop;
        const viewBottom = viewTop + thumbsCol.clientHeight;
        if (top < viewTop) {
          thumbsCol.scrollTo({ top, behavior: 'smooth' });
        } else if (bottom > viewBottom) {
          thumbsCol.scrollTo({ top: bottom - thumbsCol.clientHeight, behavior: 'smooth' });
        }
      };

      const updateCounter = (index) => {
        if (!counter || !thumbs.length) return;
        counter.textContent = `${index + 1} / ${thumbs.length}`;
      };

      const setActiveThumb = (index) => {
        activeIndex = index;
        thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        mobileSlides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        updateCounter(index);
        scrollActiveThumbIntoView();
      };

      const setMainImage = (thumb, index) => {
        if (!thumb) return;
        swapMainImage(
          mainImage,
          thumb.dataset.src,
          thumb.dataset.alt,
          thumb.dataset.srcset,
          thumb.dataset.zoomSrc
        );
        if (typeof index === 'number') setActiveThumb(index);
      };

      const goToImage = (index) => {
        if (!thumbs.length) return;
        const next = (index + thumbs.length) % thumbs.length;
        setMainImage(thumbs[next], next);
        const slide = mobileSlides[next];
        if (slide && mobileTrack) {
          mobileTrack.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        }
      };

      const openZoom = (image) => {
        const activeThumb = thumbs[activeIndex] || thumbs[0];
        const targetImage = image || mainImage || zoomImages[activeIndex];
        const src = getZoomSource(targetImage, activeThumb);
        const alt = targetImage?.alt || activeThumb?.dataset.alt || mainImage?.alt || '';
        openLightbox(src, alt);
      };

      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          goToImage(index);
        });
      });

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => goToImage(index));
      });

      zoomImages.forEach((image) => {
        image.addEventListener('click', (event) => {
          if (event.target.closest('.wishlist-btn, [data-gallery-zoom]')) return;
          event.preventDefault();
          openZoom(image);
        });
      });

      gallery.querySelector('[data-gallery-zoom]')?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openZoom(mainImage);
      });

      if (thumbsCol && isBottomThumbs) {
        thumbsCol.addEventListener(
          'wheel',
          (event) => {
            if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
            thumbsCol.scrollLeft += event.deltaY;
            event.preventDefault();
          },
          { passive: false }
        );
      }

      if (mobileTrack && mobileSlides.length) {
        let scrollTick = null;
        mobileTrack.addEventListener(
          'scroll',
          () => {
            if (scrollTick) return;
            scrollTick = window.requestAnimationFrame(() => {
              scrollTick = null;
              const scrollLeft = mobileTrack.scrollLeft;
              let nextIndex = 0;
              let minDistance = Infinity;
              mobileSlides.forEach((slide, index) => {
                const distance = Math.abs(slide.offsetLeft - scrollLeft);
                if (distance < minDistance) {
                  minDistance = distance;
                  nextIndex = index;
                }
              });
              if (nextIndex !== activeIndex) setMainImage(thumbs[nextIndex], nextIndex);
            });
          },
          { passive: true }
        );
      }

      syncThumbsHeight();
      updateCounter(activeIndex);

      if (stage && typeof ResizeObserver !== 'undefined') {
        const resizeObserver = new ResizeObserver(syncThumbsHeight);
        resizeObserver.observe(stage);
      } else {
        window.addEventListener('resize', syncThumbsHeight);
      }
    });
  };

  bindLightbox();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initProductGallery());
  } else {
    initProductGallery();
  }

  document.addEventListener('shopify:section:load', (event) => {
    bindLightbox();
    initProductGallery(event.target);
  });
})();
